"use client";

import { PagePane } from "./Pane";
import { PaneGroupContext, type PanePosition } from "./PaneGroupContext";
import { SHELL_FLUSH_CHROME } from "./shell";
import { cn } from "@/lib/utils";
import {
  useCallback,
  useId,
  useLayoutEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

export { usePaneLifecycle } from "./usePaneLifecycle";

const arePositionsEqual = (
  a: Map<string, PanePosition>,
  b: Map<string, PanePosition>
) => {
  if (a.size !== b.size) return false;
  for (const [key, value] of a) {
    const other = b.get(key);
    if (!other) return false;
    if (other.hasPreviousPane !== value.hasPreviousPane) return false;
  }
  return true;
};

const isPaneParticipatingInLayout = (paneEl: HTMLElement) => {
  if (paneEl.dataset.paneVisible !== "true") return false;

  // Some panes stay mounted/open while a responsive parent is `display: none`.
  // Ignore those so hidden breakpoint panes do not create phantom dividers.
  return paneEl.getClientRects().length > 0;
};

export const PagePaneGroupRoot = ({
  children,
  className,
  nested = false,
}: {
  children: ReactNode;
  className?: string;
  // A group inside another page frame drops its own chrome and is not a
  // page root, so the shell leaves it alone too.
  nested?: boolean;
}) => {
  const groupId = useId();
  const [container, setContainer] = useState<HTMLDivElement | null>(null);
  const [positions, setPositions] = useState<Map<string, PanePosition>>(
    () => new Map()
  );

  // Walk panes in DOM order and mark where a divider is needed. Closing panes
  // stay mounted for their width animation, so they keep their adjacent divider
  // until they unmount.
  const recomputePositions = useCallback(() => {
    if (!container) return;
    const paneEls = container.querySelectorAll<HTMLElement>(
      `[data-pane-group="${groupId}"]`
    );
    const panes = Array.from(paneEls, (paneEl) => ({
      id: paneEl.dataset.paneId ?? "",
      isVisible: isPaneParticipatingInLayout(paneEl),
    }));
    const next = new Map<string, PanePosition>();
    let hasPreviousPane = false;
    panes.forEach((pane) => {
      next.set(pane.id, {
        hasPreviousPane,
      });
      if (pane.isVisible) {
        hasPreviousPane = true;
      }
    });
    setPositions((prev) => (arePositionsEqual(prev, next) ? prev : next));
  }, [container, groupId]);

  // Recompute on every render so any structural change PaneGroup sees lands
  // before paint. Panes notify us via `notifyChange` for state changes (like
  // isExpanded flipping) that don't bubble a re-render up here.
  useLayoutEffect(() => {
    recomputePositions();
  });

  const value = useMemo(
    () => ({ groupId, container, positions, notifyChange: recomputePositions }),
    [groupId, container, positions, recomputePositions]
  );
  return (
    <PaneGroupContext.Provider value={value}>
      <div
        ref={setContainer}
        className={cn(
          "relative flex h-full min-w-0 flex-1 overflow-hidden rounded-lg border border-surface-tertiary bg-surface-primary",
          nested && "rounded-none border-0 bg-transparent",
          className,
          !nested && SHELL_FLUSH_CHROME
        )}
      >
        {children}
      </div>
    </PaneGroupContext.Provider>
  );
};

export const PagePaneGroup = Object.assign(PagePaneGroupRoot, {
  Pane: PagePane,
});
